/**
 * ui-renderers/conversations.ts
 * Conversation list rendering with drag-to-delete support.
 */

import { state } from '../state';
import { dom } from '../dom';
import { escapeHtml, escapeAttr, formatRelativeTime } from './core';

// ─────────────────────────────────────────────────────────────────────────────
// Internal: drag-to-delete state machine
// ─────────────────────────────────────────────────────────────────────────────

let dragListenersInitialized = false;
let dragState = {
  activeTile: null as HTMLElement | null,
  startX:     null as number | null,
  currentX:   0,
  dragging:   false,
};

function initGlobalDragListeners() {
  if (dragListenersInitialized) return;
  dragListenersInitialized = true;

  const onMove = (clientX: number) => {
    if (!dragState.activeTile || dragState.startX === null) return;
    const dx = clientX - dragState.startX;
    dragState.currentX = dx;
    dragState.dragging = true;
    dragState.activeTile.style.transform = `translateX(${Math.min(dx, 120)}px)`;
    dragState.activeTile.classList.toggle('drag-confirming', dx > 80);
  };

  const onEnd = (clientX: number) => {
    if (!dragState.activeTile || dragState.startX === null) return;
    const dx = clientX - dragState.startX;
    dragState.activeTile.style.transform = '';
    dragState.activeTile.classList.remove('drag-confirming');
    if (dragState.dragging && dx > 80) {
      const convId = dragState.activeTile.dataset.convId;
      if (convId && window.confirm('Delete this conversation? This cannot be undone.')) {
        document.dispatchEvent(new CustomEvent('akili:delete-conversation', { detail: convId }));
      }
    }
    dragState.activeTile = null;
    dragState.startX     = null;
    dragState.dragging   = false;
  };

  document.addEventListener('mousemove', (e) => onMove(e.clientX));
  document.addEventListener('mouseup',   (e) => onEnd(e.clientX));
  document.addEventListener('touchmove', (e) => {
    if (dragState.activeTile) onMove(e.touches[0].clientX);
  }, { passive: true });
  document.addEventListener('touchend', (e) => {
    if (dragState.activeTile) onEnd(e.changedTouches[0].clientX);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export function renderConversationList(conversations: any[]) {
  const list = dom.convList;
  if (!list) return;

  initGlobalDragListeners();

  if (!conversations || conversations.length === 0) {
    list.innerHTML = '<div class="ai-conv-empty">No conversations yet.<br>Start chatting to begin!</div>';
    return;
  }

  list.innerHTML = '';
  conversations.forEach(conv => {
    const title    = conv.title || 'Untitled conversation';
    const updated  = formatRelativeTime(conv.updated_at);
    const turns    = conv.turn_count || 0;
    const isActive = conv.id === state.conversationId;

    const tile = document.createElement('div');
    tile.className = 'ai-conv-tile' + (isActive ? ' active' : '');
    tile.setAttribute('data-conv-id', conv.id);
    tile.innerHTML = [
      '<div class="ai-conv-tile-info">',
        '<div class="ai-conv-tile-title">' + escapeHtml(title) + '</div>',
        '<div class="ai-conv-tile-meta"><span>' + updated + '</span></div>',
      '</div>',
      '<span class="ai-conv-turns">' + turns + ' turn' + (turns !== 1 ? 's' : '') + '</span>',
      '<button class="ai-conv-del" data-conv-id="' + escapeAttr(conv.id) + '" title="Delete" aria-label="Delete conversation">' + dom.TRASH_ICON + '</button>',
    ].join('');

    tile.addEventListener('click', e => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('.ai-conv-del')) return;
      document.dispatchEvent(new CustomEvent('akili:load-conversation', { detail: conv.id }));
    });

    tile.querySelector('.ai-conv-del')!.addEventListener('click', e => {
      e.stopPropagation();
      if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);
      if (window.confirm('Delete this conversation? This cannot be undone.')) {
        document.dispatchEvent(new CustomEvent('akili:delete-conversation', { detail: conv.id }));
      }
    });

    tile.dataset.convId = conv.id;
    tile.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      dragState.activeTile = tile;
      dragState.startX     = e.clientX;
      dragState.currentX   = 0;
      dragState.dragging   = false;
    });
    tile.addEventListener('touchstart', (e) => {
      dragState.activeTile = tile;
      dragState.startX     = e.touches[0].clientX;
      dragState.currentX   = 0;
      dragState.dragging   = false;
    }, { passive: true });

    list.appendChild(tile);
  });
}
